import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ArticleCard from "@/components/base/ArticleCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { paginate, searchArticles, totalPages, getHotArticles } from "@/lib/articles";
import type { ArticleCategory } from "@/mocks/articles";

const PAGE_SIZE = 6;

const typeTabs: { key: ArticleCategory | "all"; label: string; icon: string }[] = [
  { key: "all", label: "全部", icon: "ri-apps-2-line" },
  { key: "news", label: "娱乐资讯", icon: "ri-newspaper-line" },
  { key: "recommendations", label: "影视推荐", icon: "ri-film-line" },
  { key: "stars", label: "明星动态", icon: "ri-star-smile-line" },
];

export default function SearchPage() {
  useScrollToTop();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";

  const [input, setInput] = useState(query);
  const [activeType, setActiveType] = useState<ArticleCategory | "all">("all");
  const [page, setPage] = useState(1);

  useDocumentMeta(
    query ? `“${query}”的搜索结果 - 蜜桃33` : "搜索 - 蜜桃33",
    query ? `蜜桃33 站内搜索：${query} 的相关娱乐资讯、影视推荐与明星动态结果。` : undefined,
  );

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [activeType]);

  const results = useMemo(
    () => (query ? searchArticles(query, activeType) : []),
    [query, activeType],
  );

  const pages = totalPages(results.length, PAGE_SIZE);
  const currentPage = Math.min(page, pages);
  const paged = paginate(results, currentPage, PAGE_SIZE);
  const recommended = useMemo(() => getHotArticles(3), []);

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q });
  };

  return (
    <div className="w-full pb-16 pt-6">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
        <Breadcrumb items={[{ label: "搜索结果" }]} />

        <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {query ? (
              <>
                搜索「<span className="text-primary-700">{query}</span>」
              </>
            ) : (
              "站内搜索"
            )}
          </h1>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
            className="mt-4 flex flex-col gap-3 sm:flex-row"
          >
            <div className="relative flex flex-1 items-center">
              <i className="ri-search-line pointer-events-none absolute left-3.5 text-base text-foreground-400" />
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词搜索资讯、影视或明星"
                aria-label="搜索关键词"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
        </div>

        {query && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {typeTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveType(tab.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  activeType === tab.key
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
            <span className="ml-auto text-sm text-foreground-600">
              共找到 <strong className="font-semibold text-foreground-900">{results.length}</strong>{" "}
              条结果
            </span>
          </div>
        )}

        <div className="mt-6">
          {!query ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="输入关键词开始搜索"
              description="支持搜索娱乐资讯、影视作品与明星动态，例如「悬疑」「科幻」「音乐节」。"
            />
          ) : paged.length === 0 ? (
            <div className="flex flex-col gap-8">
              <EmptyState
                icon="ri-emotion-unhappy-line"
                title={`没有找到与「${query}」相关的内容`}
                description="换个关键词试试，或浏览下面的热门内容。"
              />
              <section aria-label="你可能感兴趣">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-fire-line text-primary-600" />
                  你可能感兴趣
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {recommended.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </section>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {paged.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={pages} onChange={setPage} />
            </>
          )}
        </div>

        {query && paged.length > 0 && (
          <div className="mt-10 rounded-lg bg-background-100 p-5 text-center">
            <p className="text-sm text-foreground-600">
              没有找到想要的内容？
            </p>
            <button
              type="button"
              onClick={() => navigate("/category/news")}
              className="mt-3 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览全部娱乐资讯
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}